import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';

import { filterBoolean, WorkerPool } from '@recruitee/common';
import { HttpApiService } from '@recruitee/http';
import { OfferItem, OfferFull, OfferBrief, OfferData, Position } from '@recruitee/http-types';

import { OfferScope } from '../../types';

@Injectable({
  providedIn: 'root',
})
export class TalentPoolsHttpService {
  constructor(
    private httpApi: HttpApiService,
    private workerPool: WorkerPool,
  ) {}

  public fetchCollection$(scope: string): Observable<{ offers: OfferItem[] }> {
    return this.httpApi.get(`/offers`, { kind: 'talent_pool', scope }).pipe(
      filterBoolean,
      switchMap(data =>
        data.references
          ? this.workerPool.fillInReferences(data.offers, data.references)
          : of(data.offers),
      ),
      map(offers => ({ offers })),
    );
  }

  public fetchItem$(params: {
    id: number | string;
    langCode?: string;
  }): Observable<{ offer: OfferFull }> {
    const body = { lang_code: params.langCode };
    return this.httpApi.get(`/offers/${params.id}`, body);
  }

  public fetchBriefCollection$(scope?: OfferScope): Observable<{ offers: OfferBrief[] }> {
    return this.httpApi
      .get('/offers', { kind: 'talent_pool', scope, viewMode: 'brief' })
      .pipe(filterBoolean);
  }

  public create$(data: { offer: OfferData }): Observable<{ offer: OfferFull }> {
    return this.httpApi.post('/offers/', null, data);
  }

  public update$(data: { offer: OfferData }): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${data.offer.id}`, null, data);
  }

  public follow(id: string | number): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/follow`);
  }

  public unfollow(id: string | number): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/unfollow`);
  }

  public duplicate$(id: number): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/duplicate`);
  }

  public draft(id: string | number): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/draft`);
  }

  public archive(id: string | number): Observable<{ offer: OfferFull }> {
    return this.httpApi.patch(`/offers/${id}/archive`);
  }

  public delete$(params: { id: number }): Observable<{ offer: OfferFull }> {
    return this.httpApi.delete(`/offers/${params.id}`);
  }

  public updatePositions$({ positions }: { positions: Position[] }): Observable<Position[]> {
    return this.httpApi
      .patch(`/offers/update_positions`, null, {
        offers: positions,
      })
      .pipe(map(() => positions));
  }
}
